import { t } from 'i18next';
import { FC } from 'react';
import { useHistory } from 'react-router';
import { Flex } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import { ArchiveIcon, ArrowLeftIcon, PrintIcon, ShareIcon, UndoIcon } from '@treams/treams-icons';
import {
	FeatureName,
	FeedbackRequest,
	useArchiveFeedbackRequestMutation,
	useRestoreFeedbackRequestMutation,
} from '@treams/treams-network';
import { Button, Callout, FlexGap, NoPrintWrapper } from '@treams/treams-ui';

type Props = {
	request: FeedbackRequest;
	setSharing: (sharing: boolean) => void;
};

export function feedbackResultsReturnUrl(request: FeedbackRequest, search: string) {
	const params = new URLSearchParams(search);
	if (params.has('return') && params.get('return')?.startsWith('/')) {
		return params.get('return')!;
	}
	return `/users/${request.user.id}/feedback`;
}

export const FeedbackResultsToolbar: FC<Props> = ({ request }) => {
	const history = useHistory();
	const returnUrl = feedbackResultsReturnUrl(request, history.location.search);

	return (
		<NoPrintWrapper>
			<Flex>
				<Button
					icon={<ArrowLeftIcon />}
					link={returnUrl}
					title={t('feedback.show.toolbar.back')}
					type="tertiary"
					ml="-12px"
				/>
			</Flex>
			{request.isArchived && (
				<Callout
					mb={3}
					color="red"
					icon={<ArchiveIcon />}
					title={t('feedback.show.archived.title')}
					description={t('feedback.show.archived.description')}
				/>
			)}
		</NoPrintWrapper>
	);
};

export const FeedbackResultsActions: FC<Props> = ({ request, setSharing }) => {
	const [archive, { loading: isLoadingArchive }] = useArchiveFeedbackRequestMutation({
		variables: { input: { id: request.id } },
	});
	const [restore, { loading: isLoadingRestore }] = useRestoreFeedbackRequestMutation({
		variables: { input: { id: request.id } },
	});

	const hasSharedFeedback = useFeature(FeatureName.FeedbackShare);

	return (
		<NoPrintWrapper>
			<FlexGap mt={4} gap={12} flexWrap="wrap">
				{request.user.isViewer && hasSharedFeedback && (
					<Button
						icon={<ShareIcon />}
						type="secondary"
						title={t('common.share')}
						onClick={() => setSharing(true)}
					/>
				)}
				<Button
					icon={<PrintIcon />}
					type="secondary"
					title={t('feedback.show.toolbar.print')}
					onClick={() => window.print()}
				/>
				<Button
					title={
						request.isArchived
							? t('feedback.show.toolbar.restore')
							: t('feedback.show.toolbar.archive')
					}
					type="secondary"
					icon={request.isArchived ? <UndoIcon /> : <ArchiveIcon />}
					loading={isLoadingArchive || isLoadingRestore}
					onClick={() => {
						if (request.isArchived) {
							restore();
						} else {
							archive();
						}
					}}
				/>
			</FlexGap>
		</NoPrintWrapper>
	);
};
